import React, { forwardRef, Fragment, memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import invariant from 'tiny-invariant';

import { useBoardContext } from '@v2/pages/planboard/components/Board/web/Board/context';
import { useColumnContext } from '@v2/pages/planboard/components/Board/web/column-context';
import JobCardPreview from '@v2/pages/planboard/components/JobCardPreview';
import MoveToPositionModal from '@v2/pages/planboard/components/Board/web/MoveToPositionModal';
import FinishJobModal from '@v2/components/FinishJobModal';
import { getFinishJobModalPopupConfig } from '@v2/components/FinishJobModal/helpers/popupConfig';
import useIsFinishJobEnabled from '@v2/components/FinishJobModal/hooks/useIsFinishJobEnabled';
import { ColorFunction } from '@styles/colors/types';
import { scale, width } from '@components/frame';
import { PLANBOARD_TILE_CONFIG, SCREEN_BORDER_RADIUS, BOTTOM_SHEET_TOP_OFFSET } from '@v2/pages/common/constants/base.constants';
import { Popup, JOB_STATUSES, getLinkingModalConfig, isWeb } from '@helpers';
import { usePlanboardJobNavigation } from '@v2/pages/planboard/hooks/usePlanboardJobNavigation';
import ProductionInformation from '@components/productionInformation';
import { fullWidth } from '@components/frame';
import { font } from '@styles/fonts';
import { useCardDragDrop } from './hooks/useCardDragDrop';

import type { CardPrimitiveProps, CardState } from './types';
import type { OperationSearchItem } from '@interfaces/search';

const CardPrimitive = forwardRef<HTMLDivElement, CardPrimitiveProps>(
  ({ closestEdge, item, state, isAnimating, C, placeholderHeight, isPending = false, isRunning = false, onMenuOpen, onMoveToPosition, onLinkBatch, onMarkAsFinished }, ref) => {
    const [, setType] = useState<CardState['type']>(() => state.type);
    const [isTransitionState, setIsTransitionState] = useState<boolean>(false);

    useEffect(() => {
      setType((p) => {
        if (p === 'dragging') {
          setIsTransitionState(state.type === 'idle');
        }

        return state.type;
      });
    }, [state]);

    useEffect(() => {
      const timeoutId = setTimeout(() => setIsTransitionState(false), 250);

      return () => {
        clearTimeout(timeoutId);
      };
    }, []);

    const placeholderVisibleTop = state.type === 'idle' && closestEdge === 'top';
    const placeholderVisibleBottom = state.type === 'idle' && closestEdge === 'bottom';

    const placeholderCard = (
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          border: `2px dashed ${C('Gray/Light/4')}`,
          backgroundColor: C('Gray/Light/6'),
          boxSizing: 'border-box',
        }}
      />
    );

    return (
      <Fragment>
        <div
          style={{
            height: `${placeholderHeight}px`,
            maxHeight: placeholderVisibleTop ? `${placeholderHeight}px` : 0,
            overflow: 'hidden',
            padding: placeholderVisibleTop ? `${scale(4)}px 0` : 0,
            transition: 'max-height 0.2s cubic-bezier(0.2, 0, 0, 1)',
            opacity: isTransitionState ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
          }}
          className={isAnimating ? 'card-container animate' : 'card-container'}
        >
          {placeholderVisibleTop ? placeholderCard : null}
        </div>
        <div
          ref={ref}
          style={{
            width: '100%',
            backgroundColor: C('Background/Light/Field lighter'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            position: 'relative',
            overflow: 'hidden',
            border: '0.5px solid',
            borderColor: C('Hairline/Light/No trans [mobile]'),
            transition: 'none',
            boxSizing: 'border-box',
            boxShadow: '0px 0.45px 1.35px 0px #0000000D, 0px 1.4px 5.4px 0px #0000000F, 0px 0px 8px 0px #0000001F',
            ...{
              idle: {
                cursor: isRunning ? 'default' : 'grab',
              },
              dragging: {
                pointerEvents: 'none',
              },
            }[state.type],
          }}
        >
          {state.type === 'dragging' ? (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              <div style={{ opacity: 0.35, filter: 'saturate(0.85)' }}>
                <JobCardPreview productionOperation={item} isPending={isPending} />
              </div>
            </div>
          ) : null}
          <div style={{ visibility: state.type === 'dragging' ? 'hidden' : 'visible' }}>
            <JobCardPreview productionOperation={item} isPending={isPending} onMenuOpen={onMenuOpen} onMoveToPosition={onMoveToPosition} onLinkBatch={onLinkBatch} onMarkAsFinished={onMarkAsFinished} />
          </div>
        </div>

        <div
          style={{
            height: `${placeholderHeight}px`,
            maxHeight: placeholderVisibleBottom ? `${placeholderHeight}px` : 0,
            overflow: 'hidden',
            padding: placeholderVisibleBottom ? `${scale(4)}px 0` : 0,
            transition: 'max-height 0.2s cubic-bezier(0.2, 0, 0, 1)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {placeholderVisibleBottom ? placeholderCard : null}
        </div>
      </Fragment>
    );
  }
);

export const Card = memo(
  forwardRef<HTMLDivElement | null, { item: OperationSearchItem; C: ColorFunction; isPending?: boolean }>(
    ({ item, C, isPending = false }, forwardedRef) => {
      const ref = useRef<HTMLDivElement>(null);
      const { productionOperationId } = item;

      // Running jobs are pinned at top and cannot be dragged
      const isRunning = item.status === JOB_STATUSES.RUNNING;

      const { instanceId, registerCard, moveToPosition } = useBoardContext();
      const { columnId, getCardIndex, getTotalCards } = useColumnContext();

      const isLinkedToMachine = columnId !== -1;
      const defaultCardHeight = scale(
        isLinkedToMachine ? PLANBOARD_TILE_CONFIG.CARD_HEIGHT : PLANBOARD_TILE_CONFIG.CARD_HEIGHT_UNASSIGNED
      );
      const [measuredHeight, setMeasuredHeight] = useState<number>(defaultCardHeight);
      const latestHeightRef = useRef<number>(defaultCardHeight);
      const { navigateToDocuments } = usePlanboardJobNavigation(item);
      const isFinishJobEnabled = useIsFinishJobEnabled();

      // Drag-and-drop behavior
      const { closestEdge, state, isAnimating } = useCardDragDrop({
        ref,
        productionOperationId,
        instanceId,
        isRunning,
      });

      const handleMenuOpen = useCallback(() => {
        navigateToDocuments();
      }, [navigateToDocuments]);

      const handleMoveToPosition = useCallback(() => {
        const currentPosition = getCardIndex(productionOperationId) + 1; // 1-based
        const maxPosition = getTotalCards();

        Popup({
          Component: (p: { hide: () => void }) => (
            <MoveToPositionModal
              hide={p.hide}
              maxPosition={maxPosition}
              currentPosition={currentPosition}
              onSubmit={(targetPosition) => {
                moveToPosition(productionOperationId, columnId, targetPosition);
              }}
            />
          ),
          width: 400,
          shouldCloseOnPressOutside: true,
        });
      }, [productionOperationId, columnId, getCardIndex, getTotalCards, moveToPosition]);

      const handleLinkBatch = useCallback(() => {
        Popup({
          Component: (p: { hide: () => void }) => (
            <ProductionInformation
              {...{
                ...p,
                scale,
                font,
                productionBatchId: item.productionBatchId,
                partId: item.partId,
                title: 'Link Batch to Job',
              }}
            />
          ),
          ...getLinkingModalConfig(scale),
          offsetTop: isWeb ? scale(18) : scale(BOTTOM_SHEET_TOP_OFFSET / 2),
          offsetBottom: scale(18),
          width: (isWeb ? fullWidth : width) - scale((isWeb ? fullWidth : width) > 1116 ? 36 : 0),
        });
      }, [item.productionBatchId]);

      const handleMarkAsFinished = useCallback(() => {
        if (!isFinishJobEnabled) return;

        Popup({
          Component: (p: { hide: () => void }) => <FinishJobModal hide={p.hide} item={item} machineId={columnId} />,
          ...getFinishJobModalPopupConfig(scale),
        });
      }, [isFinishJobEnabled, item, columnId]);

      useEffect(() => {
        invariant(ref.current);
        return registerCard({
          cardId: productionOperationId,
          entry: {
            element: ref.current,
          },
        });
      }, [registerCard, productionOperationId]);

      useLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
          return;
        }

        const updateHeight = (nextHeight: number) => {
          if (nextHeight <= 0) {
            return;
          }
          latestHeightRef.current = nextHeight;
          setMeasuredHeight(nextHeight);
        };

        updateHeight(element.getBoundingClientRect().height);

        if (typeof ResizeObserver === 'undefined') {
          const handleResize = () => updateHeight(element.getBoundingClientRect().height);
          window.addEventListener('resize', handleResize);
          return () => {
            window.removeEventListener('resize', handleResize);
          };
        }

        const observer = new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target === element) {
              updateHeight(entry.contentRect.height);
            }
          });
        });

        observer.observe(element);

        return () => {
          observer.disconnect();
        };
      }, []);

      const placeholderHeight = latestHeightRef.current ?? measuredHeight;

      useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(forwardedRef, () => ref.current, []);

      return (
        <Fragment>
          <CardPrimitive
            ref={ref}
            isAnimating={isAnimating}
            item={item}
            state={state}
            closestEdge={closestEdge}
            C={C}
            placeholderHeight={placeholderHeight}
            isPending={isPending}
            isRunning={isRunning}
            onMenuOpen={handleMenuOpen}
            onMoveToPosition={isRunning ? undefined : handleMoveToPosition}
            onLinkBatch={isLinkedToMachine ? handleLinkBatch : undefined}
            onMarkAsFinished={isFinishJobEnabled ? handleMarkAsFinished : undefined}
          />
        </Fragment>
      );
    }
  )
);
